import React, { useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

const WebhookResult = ({ webhookResult, authToken, title }) => {
  const { requestBody, responseBody, httpStatusCode, responseTime, responseTimeUnit, httpMethod } = webhookResult || {};
  const [isOpen, setIsOpen] = useState(true);
  const [isHeadersOpen, setIsHeadersOpen] = useState(true);
  const [isRequestBodyOpen, setIsRequestBodyOpen] = useState(true);
  const [isResponseBodyOpen, setIsResponseBodyOpen] = useState(true);
  
  // Check if we have data to display
  const hasData = requestBody || responseBody || httpStatusCode;
  
  if (!hasData) return null;
  
  // Determine status color based on HTTP status code
  const isSuccess = useMemo(() => httpStatusCode >= 200 && httpStatusCode < 300, [httpStatusCode]);
  const statusColor = isSuccess ? "text-green-600" : "text-red-600";

  // Determine response time color based on threshold
  const responseTimeColor = useMemo(() => {
    if (responseTime > 500) {
      return "text-red-600";
    } else if (responseTime > 100) {
      return "text-yellow-600";
    }
    return "text-green-600";
  }, [responseTime]);
  
  return (
    <div className="mt-4 border rounded-md bg-gray-50">
      {/* Main Accordion Header */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 rounded-t-md"
      >
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-medium text-gray-800">{title || "Webhook Test Result"}</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${isSuccess ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
            {httpStatusCode || 'N/A'}
          </span>
        </div>
        {isOpen ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {/* Accordion Content */}
      {isOpen && (
        <div className="p-4 space-y-4 border-t">
          {/* Request Section */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 mb-2">Request</h4>
            <div className="bg-white rounded-lg border">
              {/* Method Section */}
              <div className="border-b px-3 py-2">
                <div className="flex items-center">
                  <h5 className="text-xs font-medium text-gray-700 mb-0">Method:</h5>
                  <span className="text-xs ml-2 font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                    {httpMethod ? httpMethod.toUpperCase() : 'POST'}
                  </span>
                </div>
              </div>

              {/* Headers Subsection */}
              <div className="border-b">
                <button 
                  onClick={() => setIsHeadersOpen(!isHeadersOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50"
                >
                  <h5 className="text-xs font-medium text-gray-700 mb-0">Headers</h5>
                  {isHeadersOpen ? (
                    <ChevronUpIcon className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                  )}
                </button>
                {isHeadersOpen && (
                  <pre className="text-xs bg-gray-100 p-2 overflow-auto border-t mx-3 mb-3" style={{ maxHeight: '16rem' }}>
                    {JSON.stringify({
                      "Content-Type": "application/json",
                      "Authorization": authToken
                    }, null, 2)}
                  </pre>
                )}
              </div>
              
              {/* Body Subsection */}
              {requestBody && (
                <div>
                  <button 
                    onClick={() => setIsRequestBodyOpen(!isRequestBodyOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50"
                  >
                    <h5 className="text-xs font-medium text-gray-700 mb-0">Body</h5>
                    {isRequestBodyOpen ? (
                      <ChevronUpIcon className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                  {isRequestBodyOpen && (
                    <pre className="text-xs bg-gray-100 p-2 overflow-auto border-t mx-3 mb-3" style={{ maxHeight: '24rem' }}>
                      {typeof requestBody === 'string' 
                        ? requestBody 
                        : JSON.stringify(requestBody, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Response Section */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 mb-2">Response</h4>
            <div className="bg-white rounded-lg border">
              {/* Status and Response Time Subsection */}
              <div className="p-3 border-b">
                <div className="flex items-center justify-between py-2 px-3">
                  <div className="flex items-center">
                    <h5 className="text-xs font-medium text-gray-700 mb-0">Status:</h5>
                    <span className={`text-xs ml-2 font-bold ${statusColor}`}>
                      {httpStatusCode || 'N/A'}
                    </span>
                  </div>
                  
                  {responseTime && (
                    <div className="flex items-center">
                      <span className="text-xs text-gray-700">Response Time:</span>
                      <span className={`text-xs ml-1 font-bold ${responseTimeColor}`}>
                        {responseTime}
                      </span>
                      <span className="text-xs text-gray-700 ml-1">
                        {responseTimeUnit === "milliseconds" ? 'ms' : responseTimeUnit}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Response Body Subsection */}
              {responseBody && (
                <div>
                  <button 
                    onClick={() => setIsResponseBodyOpen(!isResponseBodyOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50"
                  >
                    <h5 className="text-xs font-medium text-gray-700 mb-0">Body</h5>
                    {isResponseBodyOpen ? (
                      <ChevronUpIcon className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                  {isResponseBodyOpen && (
                    <pre className="text-xs bg-gray-100 p-2 overflow-auto border-t mx-3 mb-3" style={{ maxHeight: '24rem' }}>
                      {typeof responseBody === 'string' 
                        ? responseBody 
                        : JSON.stringify(responseBody, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookResult; 